import { FC } from 'react';

import operationTypes from 'constants/operationTypes';
import { useTranslate } from 'hooks/useTranslate';
import { formatNumber } from 'utils/formats';

import { HistoryMovementsViewProvider } from 'modules/_shared/components/HistoryMovementsView/context/HistoryMovementsViewContext';
import HistoryMovementsView from 'modules/_shared/components/HistoryMovementsView';

import { ConstitutionExcelRangeMode } from '../types/ConstitutionExcel';
import { useConstitutionModal } from '../context/ConstitutionModalContext';
import { getConstitutionExcelValidationMessages } from '../utils/getConstitutionExcelValidationMessages';

const getRangeModeLabelKey = (
  rangeMode: ConstitutionExcelRangeMode
): string => {
  if (rangeMode === 'explicit') return 'ConstitutionRangeModeExplicit';
  if (rangeMode === 'mixed') return 'ConstitutionRangeModeMixed';
  return 'ConstitutionRangeModeAutomatic';
};

const ConstitutionModalPreview: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useConstitutionModal();

  const preview = data.excelPreview;

  if (!preview) {
    return null;
  }

  const errorMessages = getConstitutionExcelValidationMessages(
    preview.errors,
    t
  );
  const hasErrors = preview.errors.length > 0;
  const errorRowIndexes = new Set(
    preview.errors
      .map((error) => error.rowIndex)
      .filter((rowIndex): rowIndex is number => Boolean(rowIndex))
  );

  const renderStatusBanner = () => {
    if (hasErrors) {
      return (
        <div
          className="alert alert-danger alert-icon mb-4"
          data-testid="constitution-preview-error-banner"
        >
          <em className="icon ni ni-alert-circle" />
          <strong>
            {t('ConstitutionPreviewProblems', { count: preview.errors.length })}
          </strong>
          <ul className="constitution-modal__error-list mt-2">
            {errorMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <div
        className="alert alert-success alert-icon mb-4"
        data-testid="constitution-preview-success-banner"
      >
        <em className="icon ni ni-check-circle" />
        <span>
          {t('ConstitutionPreviewReadyToExecute', {
            partners: formatNumber(preview.totalPartners),
            shares: formatNumber(preview.totalShares),
          })}
        </span>
      </div>
    );
  };

  const renderStatCard = (
    iconClass: string,
    value: string,
    label: string,
    toneClass: string
  ) => (
    <div className="col-sm-6 col-xl-3">
      <div className="card card-bordered constitution-modal__stat-card h-100">
        <div className="card-inner py-3">
          <div className="d-flex align-items-center">
            <em className={`icon ${iconClass} ${toneClass} mr-2`} />
            <div>
              <div
                className={`constitution-modal__stat-value font-weight-bold ${toneClass}`}
              >
                {value}
              </div>
              <span className="text-soft small">{label}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!preview.items.length) {
    return (
      <div className="text-center py-5">
        <p className="text-soft mb-3">{t('ConstitutionNoPreviewItems')}</p>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={actions.handleBackToScripture}
        >
          {t('BackToScripture')}
        </button>
      </div>
    );
  }

  return (
    <div className="constitution-modal__preview">
      {renderStatusBanner()}

      <div className="row g-gs mb-3">
        {renderStatCard(
          'ni ni-user-add',
          formatNumber(preview.totalNewPartners),
          t('ConstitutionNewPartners'),
          'text-success'
        )}
        {renderStatCard(
          'ni ni-user-check',
          formatNumber(preview.totalExistingPartners),
          t('ConstitutionExistingPartners'),
          'text-info'
        )}
        {renderStatCard(
          'ni ni-layers',
          formatNumber(preview.totalShares),
          t('NºNewParticipations'),
          'text-primary'
        )}
        {renderStatCard(
          'ni ni-list-check',
          t(getRangeModeLabelKey(preview.rangeMode)),
          t('ConstitutionRangeMode'),
          'text-soft'
        )}
      </div>

      <p className="text-soft small mb-4">
        {t('Date')}: <strong>{data.date || '-'}</strong>
        {' · '}
        {t('NominalValue')}: <strong>{formatNumber(data.nominalValue)}</strong>
      </p>

      <div className="card card-bordered card-preview mb-4">
        <div className="table-responsive constitution-modal__table-scroll">
          <table className="nk-tb-list nk-tb-ulist">
            <thead className="">
              <tr className="nk-tb-item nk-tb-head tb-tnx-head fs-11px text-center">
                <th className="nk-tb-col bg-lighter">
                  {t('ConstitutionStatus')}
                </th>
                <th className="nk-tb-col bg-lighter">{t('PartnerName')}</th>
                <th className="nk-tb-col bg-lighter">{t('Cif')}</th>
                <th className="nk-tb-col bg-lighter">
                  {t('ClassOfParticipations')}
                </th>
                <th className="nk-tb-col bg-lighter">
                  {t('NºNewParticipations')}
                </th>
                <th className="nk-tb-col bg-lighter">{t('Premium')}</th>
                <th className="nk-tb-col bg-lighter">{t('OfTheNumber')}</th>
                <th className="nk-tb-col bg-lighter">{t('ToTheNumber')}</th>
              </tr>
            </thead>
            <tbody>
              {preview.items.map((item) => {
                const hasRowError = errorRowIndexes.has(item.rowIndex);
                const statusBadgeClass = item.isExistingPartner
                  ? 'badge badge-dim badge-info'
                  : 'badge badge-dim badge-success';
                const statusLabel = item.isExistingPartner
                  ? t('ConstitutionPartnerStatusExisting')
                  : t('ConstitutionPartnerStatusNew');

                return (
                  <tr
                    key={`${item.cif}-${item.rowIndex}`}
                    className={
                      hasRowError ? 'nk-tb-item table-danger' : 'nk-tb-item'
                    }
                  >
                    <td className="nk-tb-col">
                      {hasRowError && (
                        <em className="icon ni ni-alert-circle text-danger mr-1" />
                      )}
                      <span className={statusBadgeClass}>{statusLabel}</span>
                    </td>
                    <td className="nk-tb-col text-left">{item.partnerName}</td>
                    <td className="nk-tb-col text-left">{item.cif}</td>
                    <td className="nk-tb-col text-left">
                      {item.holdingClassName}
                    </td>
                    <td className="nk-tb-col text-right">
                      {formatNumber(item.shares)}
                    </td>
                    <td className="nk-tb-col text-right">
                      {formatNumber(item.sharePremium)}
                    </td>
                    <td className="nk-tb-col text-right">
                      {formatNumber(item.shareFrom)}
                    </td>
                    <td className="nk-tb-col text-right">
                      {formatNumber(item.shareTo)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3">
        <h6 className="mb-3">{t('ConstitutionMovementsSummary')}</h6>
        <HistoryMovementsViewProvider
          historyMovements={data.historyMovements}
          operationType={operationTypes.CONSTITUTION}
        >
          <HistoryMovementsView />
        </HistoryMovementsViewProvider>
      </div>
    </div>
  );
};

export default ConstitutionModalPreview;
